import '../../assets/styles/auth.scss'
import '../../assets/styles/pages.scss'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Fragment, useContext, useEffect, useState } from 'react'
import { GlobalContext, handleError } from '../../context/Provider'
import henceforthApi from '../../utils/henceforthApi'
import moment from 'moment'
import { CURRENCY_SYMBOL } from '../../utils/henceforthApi'
import { numberWithCommas } from '../../utils/validations'
import Spinner from '../../components/BootstrapCompo'
import BreadCrumb from '../../components/common/BreadCrumb'
import { toast } from 'react-toastify'
import { Modal, Button } from 'react-bootstrap'
import SignedImage from '../../components/SignedImage'

const PayoutDetail = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { authState, loading, setLoading } = useContext(GlobalContext)
    const [payout, setPayout] = useState<any>(null)
    const [showRejectModal, setShowRejectModal] = useState(false)
    const [rejectionReason, setRejectionReason] = useState('')

    let breadCrumbPath = [
        { name: 'Home', url: `/`, active: '' },
        { name: 'Payout Requests', url: `/payout-requests/1`, active: '' },
        { name: 'Payout Details', url: ``, active: 'not-allowed' }
    ]

    const fetchDetails = async () => {
        setLoading(true)
        try {
            const res = await henceforthApi.Payout.Detail({
                payout_id: id as string,
            })
            const found = res?.data?.data?.find((item: any) => item._id === id)
            if (found) {
                setPayout(found)
            } else {
                toast.error("Payout request not found")
                navigate('/payout-requests/1')
            }
        } catch (error) {
            handleError(error)
        } finally {
            setLoading(false)
        }
    }

    const handleManage = async (status: 'ACCEPTED' | 'REJECTED') => {
        if (status === 'REJECTED' && !rejectionReason) {
            toast.error("Please provide a rejection reason")
            return
        }

        setLoading(true)
        try {
            const apiRes = await henceforthApi.Payout.manage({
                payout_id: id as string,
                status,
                rejection_reason: status === 'REJECTED' ? rejectionReason : undefined
            })
            toast.success(apiRes?.message || `Payout ${status.toLowerCase()} successfully`)
            setShowRejectModal(false)
            fetchDetails() // Refresh data
        } catch (error) {
            handleError(error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchDetails()
    }, [id])

    if (!payout && !loading) return <div className="p-5 text-center">Loading...</div>

    return (
        <>
            <BreadCrumb pathNameDeclare={breadCrumbPath} />

            <div className='page-spacing'>
                <div className="container-fluid">
                    <div className="row">
                        {/* Seller Info Card */}
                        <div className="col-md-4">
                            <div className="common-card mb-4">
                                <div className="common-card-title d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">Seller Information</h5>
                                    <span className={`badge bg-${payout?.status === 'PENDING' ? 'warning' : payout?.status === 'ACCEPTED' ? 'success' : 'danger'}`}>
                                        {payout?.status}
                                    </span>
                                </div>
                                <div className="common-card-content text-center p-4">
                                    <SignedImage
                                        src={payout?.seller_details?.image || ""}
                                        fallback={payout?.seller_details?.name}
                                        alt="img"
                                        className='rounded-circle'
                                        size="small"
                                    />
                                    <h4 className="mb-1">{payout?.seller_details?.name}</h4>
                                    <p className="text-muted mb-3">{payout?.seller_details?.email}</p>
                                    <hr />
                                    <div className="row text-start mt-3">
                                        <div className="col-6 mb-2 text-muted">Requested Amount:</div>
                                        <div className="col-6 mb-2 fw-bold text-theme">{CURRENCY_SYMBOL} {numberWithCommas(payout?.amount || 0)}</div>
                                        <div className="col-6 mb-2 text-muted">Requested On:</div>
                                        <div className="col-6 mb-2 small">{moment(payout?.created_at).format('DD MMM YYYY, hh:mm A')}</div>
                                    </div>

                                    {payout?.status === 'PENDING' && (
                                        <div className="mt-4 d-grid gap-2">
                                            <button className="btn btn-success" onClick={() => handleManage('ACCEPTED')}>
                                                <i className="fa fa-check me-2"></i> Approve Payout
                                            </button>
                                            <button className="btn btn-outline-danger" onClick={() => setShowRejectModal(true)}>
                                                <i className="fa fa-times me-2"></i> Reject Payout
                                            </button>
                                        </div>
                                    )}

                                    {payout?.status === 'REJECTED' && (
                                        <div className="mt-3 p-3 bg-danger bg-opacity-10 border border-danger rounded text-start">
                                            <small className="text-danger fw-bold d-block mb-1">Rejection Reason:</small>
                                            <p className="mb-0 small">{payout?.rejection_reason || 'No reason provided'}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Orders List Card */}
                        <div className="col-md-8">
                            <div className="common-card">
                                <div className="common-card-title">
                                    <h5 className="mb-0">Included Orders ({payout?.orders?.length || 0})</h5>
                                </div>
                                <div className="common-card-content">
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Product</th>
                                                    <th>Quantity</th>
                                                    <th>Price</th>
                                                    <th>Earnings</th>
                                                    <th>Status</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {payout?.orders?.map((order: any) => {
                                                    const product = order?.products_data?.[0];
                                                    return (
                                                        <tr key={order._id}>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <SignedImage
                                                                        src={product?.image || ""}
                                                                        fallback={product?.name}
                                                                        alt=""
                                                                        className="rounded me-2"
                                                                        size="small"
                                                                    />
                                                                    <div className='d-flex flex-column'>
                                                                        <div className="small fw-bold">{product?.name || 'N/A'}</div>
                                                                        <small className="text-muted">Product ID: {order.product_order_id}</small>
                                                                        <small className="text-muted">Order ID: {order.order_id}</small>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>{order.quantity}</td>
                                                            <td>{CURRENCY_SYMBOL}{numberWithCommas(order.price)}</td>
                                                            <td className="text-success fw-bold">{CURRENCY_SYMBOL}{numberWithCommas(order.total_earnings || 0)}</td>
                                                            <td>
                                                                <span className="badge rounded-pill bg-light text-dark border">
                                                                    {order.order_status}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <Link to={`/ordersdetails/${order.order_id}`} className="btn btn-theme btn-sm shadow-none">
                                                                    <i className="fa fa-eye"></i>
                                                                </Link>
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* Financial Summary Section */}
                            {/* <div className="common-card mt-4">
                                <div className="common-card-title">
                                    <h5 className="mb-0">Payout Financial Summary</h5>
                                </div>
                                <div className="common-card-content p-4">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="p-3 bg-light rounded h-100">
                                                <h6 className="fw-bold mb-3 border-bottom pb-2 text-uppercase small text-muted">Summary of all Orders</h6>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span>Total Product Price:</span>
                                                    <span className="fw-bold">{CURRENCY_SYMBOL}{numberWithCommas(payout?.orders?.reduce((sum: number, o: any) => sum + (o.price * o.quantity), 0) || 0)}</span>
                                                </div> */}

                            {/* <div className="d-flex justify-content-between mb-2">
                                                    <span>Total Commission:</span>
                                                    <span className="text-danger fw-bold">-{CURRENCY_SYMBOL}{numberWithCommas(payout?.orders?.reduce((sum: number, o: any) => sum + (o.admin_commision || 0), 0) || 0)}</span>
                                                </div> */}
                            {/* <div className="d-flex justify-content-between mb-2">
                                                    <span>Total Commission:</span>
                                                    <span className="text-danger fw-bold">-{CURRENCY_SYMBOL}{numberWithCommas(payout?.orders?.reduce((sum: number, o: any) => sum + (o.admin_commision || 0), 0) || 0)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span>Total Tax:</span>
                                                    <span className="text-danger fw-bold">-{CURRENCY_SYMBOL}{numberWithCommas(payout?.orders?.reduce((sum: number, o: any) => sum + (o.tax_amount || 0), 0) || 0)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-2 border-top pt-2"></div>
                                                <div className="d-flex justify-content-between mb-2 border-top pt-2">
                                                    <span className="fw-bold">Total Request Amount:</span>
                                                    <span className="text-primary h5 mb-0 fw-bold">{CURRENCY_SYMBOL}{numberWithCommas(payout?.amount || 0)}</span>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div> */}


                        </div>
                    </div>
                </div>
            </div>

            {/* Reject Modal */}
            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Reject Payout Request</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label">Reason for Rejection</label>
                        <textarea
                            className="form-control"
                            rows={4}
                            placeholder="Enter the reason why this payout is being rejected..."
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                        ></textarea>
                        <small className="text-muted">This reason will be visible to the seller.</small>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={() => handleManage('REJECTED')}>
                        Confirm Rejection
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default PayoutDetail;
